<template>
  <header class="max-w-7xl mx-auto px-4 sm:px-6 pb-4 sm:pb-8 mt-0 sm:mt-16">
    <!-- Desktop Navigation (Unchanged) -->
    <nav 
      class="hidden sm:flex sm:flex-row sm:items-center sm:justify-between gap-4"
      aria-label="Tool navigation and actions"
    >
      <!-- Back Button -->
      <Link 
        href="/tools" 
        class="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-all group px-3 py-1.5 rounded-lg hover:bg-gray-800/50 transform hover:scale-[1.02] active:scale-[0.98]"
        aria-label="Back to all tools"
      >
        <Icon 
          icon="heroicons:arrow-long-left" 
          class="w-5 h-5 transition-transform duration-300 ease-out group-hover:-translate-x-1"
          aria-hidden="true"
        />
        <span class="font-medium">All Tools</span>
      </Link>

      <!-- Right Side - Actions -->
      <div 
        class="flex items-center bg-gray-800/30 backdrop-blur-sm rounded-lg px-2 transform hover:bg-gray-800/40 transition-all duration-300 ease-out"
        role="toolbar"
        aria-label="Tool actions"
      >
        <!-- Favorite Button Group -->
        <div 
          class="flex items-center gap-2"
          role="group"
          aria-label="Tool management"
        >
            <FavoriteButtonModal 
              :toolId="tool.id" 
              :initialIsFavorite="tool.is_favorite" 
              :initialFavoritesCount="tool.favorites_count"
              class="p-2 text-gray-400 hover:text-white transition-all rounded-md hover:bg-gray-700/50 transform hover:scale-110 active:scale-95"
            />
          <Link
            href="/contact"
            class="p-2 text-gray-400 hover:text-white transition-all rounded-md hover:bg-gray-700/50 transform hover:scale-110 active:scale-95"
            aria-label="Report issue"
          >
            <Icon icon="heroicons:flag" class="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>

        <!-- Divider -->
        <div class="mx-2 h-6 w-px bg-gray-700/50" role="separator" />

        <!-- Share Actions Group -->
        <div 
          class="flex items-center gap-2"
          role="group"
          aria-label="Share tool"
        >
          <button
            v-for="share in socialShares"
            :key="share.platform"
            @click="shareOnPlatform(share.platform)"
            :class="[
              'p-2 text-gray-400 transition-all rounded-md hover:bg-gray-700/50 transform hover:scale-110 active:scale-95',
              {
                'hover:text-[#1DA1F2]': share.platform === 'twitter',
              }
            ]"
            :aria-label="`Share on ${share.platform}`"
          >
            <Icon :icon="share.icon" class="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <!-- Divider -->
        <div class="mx-2 h-6 w-px bg-gray-700/50" role="separator" />

        <!-- Copy URL Button -->
        <button
          @click="copyToClipboard"
          class="p-2 text-gray-400 hover:text-white transition-all rounded-md hover:bg-gray-700/50 flex items-center gap-1.5 group transform hover:scale-105 active:scale-95"
          aria-label="Copy URL to clipboard"
        >
          <Icon 
            :icon="copied ? 'heroicons:clipboard-document-check' : 'heroicons:clipboard'" 
            class="w-5 h-5 transition-transform duration-300 group-hover:rotate-[-4deg]"
            :class="{ 'text-green-400': copied }"
          />
          <span class="text-sm hidden sm:inline transition-all duration-300 group-hover:text-blue-400">
            Copy URL
          </span>
        </button>
      </div>
    </nav>

    <!-- Mobile Navigation (Reversed Order) -->
    <nav 
      class="sm:hidden flex flex-col gap-2"
      aria-label="Mobile tool navigation"
    >
      <!-- Back Button -->
      <div class="flex justify-center">
        <Link 
          href="/tools" 
          class="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-all group px-3 py-1.5 rounded-lg hover:bg-gray-800/50"
          aria-label="Back to all tools"
        >
          <Icon 
            icon="heroicons:arrow-long-left" 
            class="w-5 h-5" 
            aria-hidden="true"
          />
          <span class="font-medium">All Tools</span>
        </Link>
      </div>

      <!-- Actions Bar -->
      <div 
        class="flex items-center justify-between bg-gray-800/30 backdrop-blur-sm rounded-lg px-3 py-1.5"
        role="toolbar"
        aria-label="Tool actions"
      >
        <!-- Left Side Actions (Now has Share and Copy) -->
        <div 
          class="flex items-center gap-3"
          role="group"
          aria-label="Share actions"
        >
          <button
            v-for="share in socialShares"
            :key="share.platform"
            @click="shareOnPlatform(share.platform)"
            :class="[
              'text-gray-400',
              {
                'hover:text-[#1DA1F2]': share.platform === 'twitter',
              }
            ]"
            :aria-label="`Share on ${share.platform}`"
          >
            <Icon :icon="share.icon" class="w-5 h-5" aria-hidden="true" />
          </button>
          <button
            @click="copyToClipboard"
            class="text-gray-400 hover:text-white"
            aria-label="Copy URL to clipboard"
          >
            <Icon 
              :icon="copied ? 'heroicons:clipboard-document-check' : 'heroicons:clipboard'" 
              class="w-5 h-5"
              :class="{ 'text-green-400': copied }"
            />
          </button>
        </div>

        <!-- Right Side Actions (Now has Favorite and Report) -->
        <div 
          class="flex items-center gap-3"
          role="group"
          aria-label="Tool management"
        >
          <FavoriteButtonModal 
            :toolId="tool.id" 
            :initialIsFavorite="tool.is_favorite" 
            :initialFavoritesCount="tool.favorites_count"
            class="text-gray-400 hover:text-white"
          />
          <Link
            href="/contact"
            class="text-gray-400 hover:text-white"
            aria-label="Report issue"
          >
            <Icon icon="heroicons:flag" class="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import { Icon } from '@iconify/vue';
import FavoriteButtonModal from '@/Components/FavoriteButtonModal.vue';

const props = defineProps({
  tool: {
    type: Object,
    required: true,
    validator: (tool) => {
      return tool.id && typeof tool.name === 'string';
    }
  }
});

// Social share configurations
const socialShares = [
  {
    platform: 'twitter',
    icon: 'ri:twitter-x-fill',
    shareUrl: (text, url) => `https://twitter.com/intent/tweet?text=${text}&url=${url}`
  },
];

// Share functionality
const shareOnPlatform = (platform) => {
  const url = encodeURIComponent(window.location.href);
  const text = encodeURIComponent(`Check out ${props.tool.name} on Balan.ai`);
  const share = socialShares.find(s => s.platform === platform);
  
  if (share) {
    const shareUrl = share.shareUrl(text, url);
    window.open(shareUrl, '_blank', 'width=600,height=400');
  }
};

const copied = ref(false);

// Clipboard functionality
const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href);
    copied.value = true;
    setTimeout(() => copied.value = false, 1000);
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};

</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

header {
  animation: fadeIn 0.5s ease-out;
}
</style>